'use client';

import Image from 'next/image';

import { ArrowRight, Building2, CalendarClock, FileText, Shield } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import commercialOffice from '@/public/images/serviceRelated/commercial-office.webp';

const TRUST_KEYS = ['insured', 'afterHours', 'invoice'] as const;

const TRUST_ICONS = {
  insured: Shield,
  afterHours: CalendarClock,
  invoice: FileText,
} as const;

function headlineLines(headline: string): string[] {
  return headline
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

export function OfficeCleaningHero() {
  const t = useTranslations('servicesPage.officeCleaningLanding');
  const reducedMotion = useReducedMotion();

  const lines = headlineLines(t('headline'));
  const trustItems = TRUST_KEYS.map(key => ({
    key,
    label: t(`trust.${key}`),
    icon: TRUST_ICONS[key],
  }));

  return (
    <section className='relative isolate min-h-[min(88vh,42rem)] overflow-hidden border-b border-oxford-blue/10 bg-oxford-blue'>
      <Image
        src={commercialOffice}
        alt={t('imageAlt')}
        fill
        priority
        quality={90}
        sizes='100vw'
        className='object-cover object-[center_35%]'
        placeholder='blur'
      />
      <div
        className='absolute inset-0 bg-gradient-to-r from-oxford-blue/95 via-oxford-blue/78 to-oxford-blue/35'
        aria-hidden
      />
      <div
        className='absolute inset-0 bg-gradient-to-t from-oxford-blue/90 via-oxford-blue/25 to-oxford-blue/20'
        aria-hidden
      />

      <UnifiedContainer
        size='xl'
        padding='lg'
        className='relative flex min-h-[min(88vh,42rem)] items-end pb-12 pt-24 sm:items-center sm:pb-16 sm:pt-28'
      >
        <motion.div
          className='max-w-2xl space-y-5 sm:space-y-6'
          initial={reducedMotion ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className='inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-seasalt/90 backdrop-blur-sm'>
            <Building2 className='h-3.5 w-3.5 text-orange-peel' aria-hidden />
            {t('eyebrow')}
          </div>

          <h1
            id='office-cleaning-hero-heading'
            className='text-pretty text-[clamp(2rem,6vw,3.5rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-seasalt'
          >
            {lines.map((line, index) => (
              <span
                key={line}
                className={cn('block', index === lines.length - 1 && 'text-orange-peel')}
              >
                {line}
              </span>
            ))}
          </h1>

          <p className='max-w-xl text-pretty text-base leading-relaxed text-seasalt/85 sm:text-lg'>
            {t('subtitle')}
          </p>

          <motion.div
            className='flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap sm:items-center'
            initial={reducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              href='/book?service=office_cleaning'
              className='inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-orange-peel px-6 text-sm font-bold text-white shadow-[0_16px_40px_-12px_rgba(249,115,22,0.55)] transition-colors hover:bg-orange-peel/90 sm:h-14 sm:text-base'
            >
              {t('primaryCta')}
              <ArrowRight className='h-4 w-4' aria-hidden />
            </Link>
            <Link
              href='/contact'
              className='inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/35 bg-white/10 px-6 text-sm font-semibold text-seasalt backdrop-blur-sm transition-colors hover:bg-white/15 sm:h-14 sm:text-base'
            >
              {t('secondaryCta')}
            </Link>
          </motion.div>

          <ul className='flex flex-col gap-2 border-t border-white/15 pt-5 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2'>
            {trustItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.li
                  key={item.key}
                  className='flex items-center gap-2 text-sm font-medium text-seasalt/80'
                  initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: reducedMotion ? 0 : 0.18 + index * 0.06,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <Icon className='h-4 w-4 shrink-0 text-orange-peel' aria-hidden />
                  {item.label}
                </motion.li>
              );
            })}
          </ul>
        </motion.div>
      </UnifiedContainer>
    </section>
  );
}
